import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import AdminLayout from "../../components/AdminLayout";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const adminToken = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [adminToken]);

  const fetchData = async () => {
    try {
      const [statsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API}/admin/reports?status=pending`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        })
      ]);
      setStats(statsRes.data);
      setRecentReports(reportsRes.data.slice(0, 5));
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Error fetching dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-rose-500 font-serif text-2xl">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Admin Dashboard" subtitle="Moderation overview and quick actions">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 lg:mb-8">
        <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <span className="text-xl sm:text-2xl">👥</span>
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{stats?.users?.total || 0}</div>
          <p className="text-slate-400 text-xs sm:text-sm">Total Users</p>
        </div>

        <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
              <span className="text-xl sm:text-2xl">⚠️</span>
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{stats?.reports?.pending || 0}</div>
          <p className="text-slate-400 text-xs sm:text-sm">Pending Reports</p>
        </div>

        <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <span className="text-xl sm:text-2xl">🚫</span>
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{stats?.users?.banned || 0}</div>
          <p className="text-slate-400 text-xs sm:text-sm">Banned Users</p>
        </div>

        <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <span className="text-xl sm:text-2xl">✓</span>
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{stats?.users?.id_verified || 0}</div>
          <p className="text-slate-400 text-xs sm:text-sm">ID Verified</p>
        </div>
      </div>

      {/* Reports by Type & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 lg:mb-8">
        <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-700">
          <h2 className="font-serif text-lg sm:text-xl text-white mb-4 sm:mb-6">Reports by Type</h2>
          <div className="space-y-3 sm:space-y-4">
            {Object.entries(stats?.reports?.by_type || {}).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="text-slate-400 capitalize text-sm sm:text-base">{type.replace('_', ' ')}</span>
                <span className="text-white font-medium">{count}</span>
              </div>
            ))}
            {Object.keys(stats?.reports?.by_type || {}).length === 0 && (
              <p className="text-slate-500 text-center py-4 text-sm">No reports yet</p>
            )}
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-700">
          <h2 className="font-serif text-lg sm:text-xl text-white mb-4 sm:mb-6">Quick Actions</h2>
          <div className="space-y-4">
            <Button
              onClick={async () => {
                try {
                  const response = await axios.post(`${API}/admin/send-match-emails`, {}, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                  });
                  toast.success(`Match emails sent to ${response.data.sent} users!`);
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Failed to send match emails");
                }
              }}
              className="w-full bg-rose-500 hover:bg-rose-600 text-white text-sm sm:text-base"
              data-testid="send-match-emails-btn"
            >
              💕 Send Weekly Match Emails
            </Button>
            <div className="text-slate-400 text-xs sm:text-sm">
              <p className="mb-2 font-medium">Love Stories</p>
              <div className="flex items-center justify-between">
                <span>Pending Approval</span>
                <span className="text-amber-400 font-medium">{stats?.love_stories?.pending || 0}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span>Approved</span>
                <span className="text-emerald-400 font-medium">{stats?.love_stories?.approved || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Reports */}
      <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-700">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="font-serif text-lg sm:text-xl text-white">Recent Pending Reports</h2>
          <Link to="/admin/reports" className="text-rose-400 hover:text-rose-300 text-xs sm:text-sm">
            View All →
          </Link>
        </div>

        {recentReports.length === 0 ? (
          <div className="text-center py-8">
            <span className="text-4xl block mb-4">✅</span>
            <p className="text-slate-400 text-sm">No pending reports! Great job keeping the community safe.</p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {recentReports.map((report) => (
              <div 
                key={report.id}
                className="flex items-center justify-between p-3 sm:p-4 bg-slate-900/50 rounded-xl gap-3"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    report.report_type === 'safety_concern' ? 'bg-red-500/10' :
                    report.report_type === 'user_complaint' ? 'bg-amber-500/10' :
                    'bg-blue-500/10'
                  }`}>
                    <span className="text-lg">⚠️</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium capitalize text-sm sm:text-base truncate">
                      {report.report_type.replace('_', ' ')}
                    </p>
                    <p className="text-slate-400 text-xs sm:text-sm truncate">
                      {report.category || 'General'} • {new Date(report.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate(`/admin/reports/${report.id}`)}
                  className="bg-slate-700 text-white hover:bg-slate-600 text-xs sm:text-sm flex-shrink-0"
                >
                  Review
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
